import { supabase } from './supabase';
import Compressor from 'compressorjs';

// Helper function to check if file is HEIC/HEIF format
export function isHEICFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();
  return fileType === 'image/heic' || 
         fileType === 'image/heif' || 
         fileName.endsWith('.heic') || 
         fileName.endsWith('.heif');
}

// Helper function to convert image to WebP format
async function convertToWebP(file: File, quality: number = 0.8): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const webpFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
                type: 'image/webp',
                lastModified: Date.now(),
              });
              resolve(webpFile);
            } else {
              reject(new Error('Failed to convert to WebP'));
            }
          },
          'image/webp',
          quality
        );
      } else {
        reject(new Error('Canvas context not available'));
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

// Helper function to check WebP support
function supportsWebP(): Promise<boolean> {
  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
}

export type StorageBucket = 
  | 'avatars' 
  | 'banners' 
  | 'community-images' 
  | 'post-images' 
  | 'post-videos'
  | 'listing-images'
  | 'event-images'
  | 'venue-images'
  | 'job-images'
  | 'certification-images'
  | 'blog-images';

// Function to get video duration
export async function getVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    
    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    
    video.src = URL.createObjectURL(file);
  });
}

export async function uploadMedia(file: File, bucket: StorageBucket): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user found');

    // SECURITY: Validate file size BEFORE processing (20MB for videos, 10MB for images)
    const MAX_VIDEO_SIZE = 20 * 1024 * 1024; // 20MB
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

    const isVideo = file.type.startsWith('video/');
    // Accept HEIC/HEIF files (common on iPhones) along with standard image types
    const isImage = file.type.startsWith('image/') ||
                    file.type === 'image/heic' ||
                    file.type === 'image/heif' ||
                    file.name.toLowerCase().endsWith('.heic') ||
                    file.name.toLowerCase().endsWith('.heif');

    if (!isVideo && !isImage) {
      throw new Error('Only image and video files are supported');
    }

    // SECURITY: Check file size
    if (isVideo && file.size > MAX_VIDEO_SIZE) {
      throw new Error('Video file is too large. Maximum size is 20MB. Please compress your video.');
    }
    if (isImage && file.size > MAX_IMAGE_SIZE) {
      throw new Error('Image file is too large. Maximum size is 10MB. Please use a smaller image.');
    }

    // SECURITY: Validate file extension against whitelist
    const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
    const ALLOWED_IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
    const ALLOWED_VIDEO_EXTS = ['mp4', 'mov', 'avi', 'webm'];

    if (isImage && !ALLOWED_IMAGE_EXTS.includes(fileExt)) {
      throw new Error(`Invalid image format. Allowed formats: ${ALLOWED_IMAGE_EXTS.join(', ')}`);
    }
    if (isVideo && !ALLOWED_VIDEO_EXTS.includes(fileExt)) {
      throw new Error(`Invalid video format. Allowed formats: ${ALLOWED_VIDEO_EXTS.join(', ')}`);
    }

    // Check video duration if it's a video file
    if (isVideo) {
      try {
        const duration = await getVideoDuration(file);
        if (duration > 15) {
          throw new Error('Video must be 15 seconds or shorter');
        }
      } catch (error) {
        throw new Error('Unable to process video file. Please try a different video.');
      }
    }

    let processedFile = file;

    // Compress only if it's an image
    if (isImage) {
      // Check if browser supports WebP
      const webpSupported = await supportsWebP();

      if (webpSupported && file.type !== 'image/webp') {
        try {
          // First compress the image aggressively to reduce size
          const compressedFile = await compressImage(file, {
            quality: 0.85, // Good balance between quality and size
            maxWidth: bucket === 'avatars' ? 500 : 1920,
            maxHeight: bucket === 'avatars' ? 500 : 1920,
            convertSize: Infinity // Don't auto-convert to JPEG
          });

          // Then convert to WebP (better compression)
          processedFile = await convertToWebP(compressedFile, 0.80);
        } catch (webpError) {
          console.warn('WebP conversion failed, falling back to standard compression:', webpError);
          // Fallback to aggressive standard compression
          processedFile = await compressImage(file, {
            quality: 0.75, // More aggressive compression
            maxWidth: bucket === 'avatars' ? 500 : 1920,
            maxHeight: bucket === 'avatars' ? 500 : 1920,
            convertSize: 500000 // Convert to JPEG if > 500KB
          });
        }
      } else {
        // Standard compression for non-WebP browsers or already WebP files
        processedFile = await compressImage(file, {
          quality: 0.75,
          maxWidth: bucket === 'avatars' ? 500 : 1920,
          maxHeight: bucket === 'avatars' ? 500 : 1920,
          convertSize: 500000
        });
      }
    }

    // SECURITY: Use crypto.randomUUID() for secure unique filenames
    const secureFileName = `${user.id}/${crypto.randomUUID()}.${processedFile.type === 'image/webp' ? 'webp' : fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(secureFileName, processedFile);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(secureFileName);

    return publicUrl;
  } catch (error) {
    console.error(`Error uploading to ${bucket}:`, error);
    throw error;
  }
}

export async function compressImage(file: File, options: {
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  convertSize?: number;
  success?: (result: File) => void;
  error?: (err: any) => void;
} = {}): Promise<File> {
  // Note: Compressorjs automatically handles HEIC/HEIF files from iPhones
  // and converts them to JPEG/PNG format
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: options.quality || 0.8, // 80% quality by default
      maxWidth: options.maxWidth || 1920, // Max width 1920px by default
      maxHeight: options.maxHeight || 1080, // Max height 1080px by default
      convertSize: options.convertSize || 1000000, // Convert to JPG if > 1MB by default
      mimeType: 'auto', // Auto-detect and convert formats (handles HEIC)
      success: (result) => {
        // Create a new File object from the compressed Blob
        const compressedFile = new File([result], file.name, {
          type: result.type,
          lastModified: Date.now(),
        });
        
        if (options.success) {
          options.success(compressedFile);
        }
        
        resolve(compressedFile);
      },
      error: (err) => {
        console.error('Image compression error:', err);
        
        if (options.error) {
          options.error(err);
        }
        
        // If compression fails, return original file
        resolve(file);
      },
    });
  });
}

export async function uploadImage(file: File, bucket: StorageBucket): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user found');

    // SECURITY: Validate file size (10MB max)
    const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_IMAGE_SIZE) {
      throw new Error('Image file is too large. Maximum size is 10MB.');
    }

    // SECURITY: Validate file extension
    const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
    const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
    if (!ALLOWED_EXTS.includes(fileExt)) {
      throw new Error(`Invalid image format. Allowed formats: ${ALLOWED_EXTS.join(', ')}`);
    }

    // Check if browser supports WebP
    const webpSupported = await supportsWebP();
    let processedFile = file;

    if (webpSupported && file.type !== 'image/webp') {
      try {
        // First compress the image aggressively
        const compressedFile = await compressImage(file, {
          quality: 0.85,
          maxWidth: bucket === 'avatars' ? 500 : 1920,
          maxHeight: bucket === 'avatars' ? 500 : 1920,
          convertSize: Infinity // Don't auto-convert to JPEG
        });

        // Then convert to WebP for better compression
        processedFile = await convertToWebP(compressedFile, 0.80);
      } catch (webpError) {
        console.warn('WebP conversion failed, falling back to standard compression:', webpError);
        // Fallback to aggressive standard compression
        processedFile = await compressImage(file, {
          quality: 0.75,
          maxWidth: bucket === 'avatars' ? 500 : 1920,
          maxHeight: bucket === 'avatars' ? 500 : 1920,
          convertSize: 500000
        });
      }
    } else {
      // Standard compression for non-WebP browsers or already WebP files
      processedFile = await compressImage(file, {
        quality: 0.75,
        maxWidth: bucket === 'avatars' ? 500 : 1920,
        maxHeight: bucket === 'avatars' ? 500 : 1920,
        convertSize: 500000
      });
    }

    // SECURITY: Use crypto.randomUUID() for secure unique filenames
    const secureFileName = `${user.id}/${crypto.randomUUID()}.${processedFile.type === 'image/webp' ? 'webp' : fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(secureFileName, processedFile);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(secureFileName);

    return publicUrl;
  } catch (error) {
    console.error(`Error uploading to ${bucket}:`, error);
    return null;
  }
}